import type { ReviewNoteDocType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import { defineRxdbCollectionModel } from './migration';

// ============================================================================
// RxDB collection schema for coach-review per-day notes.
//
// Document shape mirrors the D1 `review_notes` row. Uses `updatedAtUtc` as the
// checkpoint field. Owned by the athlete's user; a coach writing through a
// share link creates rows with `author: 'coach'` server-side, which then sync
// down to the athlete's client.
// ============================================================================

export const REVIEW_NOTES_COLLECTION = 'reviewNotes' as const;

export const reviewNotesSchemaLiteral = {
  title: 'review notes schema',
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    userId: { type: 'string', maxLength: 100 },
    date: { type: 'string', maxLength: 10 },
    author: { type: 'string', enum: ['athlete', 'coach'] },
    body: { type: 'string' },
    createdAtUtc: { type: 'string', maxLength: 30 },
    updatedAtUtc: { type: 'string', maxLength: 30 },
    deletedAt: { type: ['string', 'null'] },
  },
  required: ['id', 'userId', 'date', 'author', 'body', 'createdAtUtc', 'updatedAtUtc', 'deletedAt'],
  indexes: [['userId', 'updatedAtUtc'], ['userId', 'date']],
} as const;

export const REVIEW_NOTES_SCHEMA_VERSION = reviewNotesSchemaLiteral.version;

export interface ReviewNotesCheckpoint {
  updatedAtUtc: string;
  id: string;
}

type _ReviewNoteSchemaDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof reviewNotesSchemaLiteral>;
type _ReviewNoteDocTypeInSync = Assert<AssertEqual<_ReviewNoteSchemaDocType, ReviewNoteDocType>>;

export const reviewNotesSchema: RxJsonSchema<ReviewNoteDocType> = reviewNotesSchemaLiteral;

export const reviewNotesModel = defineRxdbCollectionModel<typeof REVIEW_NOTES_COLLECTION, ReviewNoteDocType>({
  collection: REVIEW_NOTES_COLLECTION,
  schema: reviewNotesSchema,
});
